import S3 from "aws-sdk/clients/s3";

import {
  getDownloadPresignedUrl,
  getUploadPresignedUrl,
  uploadFileOnPresignedUrl,
} from "../setup/api/services/file";
import { AWS_S3_BASE_URL, AWS_S3_BUCKET } from "./constants";
import { EUploadImageType, IUploadResult, UploadProgress } from "./enums";
import { ManagedUpload } from "aws-sdk/clients/s3";

export const DOWNLOAD_URL_EXPIRATION = 604800; // 7 days
const UPLOAD_URL_EXPIRATION = 36000;

const s3 = new S3({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECURITY_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
});

/** Bucket key prefix per upload target. */
const keyPrefix = (type: EUploadImageType, id: string | number): string => {
  if (type === EUploadImageType.CONTRACT) return `images/contracts/${id}`;
  if (type === EUploadImageType.ORGANIZATION)
    return `images/organizations/${id}`;
  return `images/users/${id}`;
};

/**
 * Builds a unique object key, PDFs included.
 *
 * Keying a PDF on its original file name silently overwrote any earlier upload of the same name
 * under the same id — two documents both called "scan.pdf" left two records pointing at one
 * object, having lost the first. Callers keep the original name separately (e.g. an attachment's
 * `description`), so nothing is lost by renaming the object.
 */
const buildObjectKey = (
  type: EUploadImageType,
  id: string | number,
  fileName: string
): string => {
  const lastDot = fileName.lastIndexOf(".");
  const ext = lastDot > 0 ? fileName.slice(lastDot + 1) : "";
  const unique = `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  return `${keyPrefix(type, id)}/${unique}${ext ? `.${ext}` : ""}`;
};

/**
 * Uploads via a presigned PUT rather than the embedded AWS credentials used by `uploadFile`,
 * and returns the bucket/key so callers can register the object with the backend.
 *
 * Ported from the admin portal so both apps write organization attachments the same way; the
 * `/organizations/{code}/attachments` endpoint expects bucketName + objectName, not a URL.
 */
export const uploadFileWithPresignedUrl = async (
  file: File,
  type: EUploadImageType,
  id: string | number,
  progressCallback?: (progress: UploadProgress) => void
): Promise<IUploadResult> => {
  const bucket = AWS_S3_BUCKET;
  const key = buildObjectKey(type, id, file.name);

  const presignedUrl = await getUploadPresignedUrl({
    bucket_name: bucket,
    object_name: key,
    expiration: UPLOAD_URL_EXPIRATION,
    // Private object. These carry compliance documents (IBAN certificates, commercial
    // registers), so the object must not be readable by anyone holding the key — reads go
    // through a presigned GET instead. Note the admin portal still uploads these as public.
    is_public: false,
  });

  const response = await uploadFileOnPresignedUrl({
    presignedUrl,
    file,
    progressCallback,
  });

  if (response.status !== 200) {
    throw new Error("Failed to upload file");
  }

  const url = await getDownloadPresignedUrl({
    bucket_name: bucket,
    object_name: key,
    expiration: DOWNLOAD_URL_EXPIRATION,
  });

  return { url, bucket, key };
};

export const uploadFile = async (
  image: File,
  type: EUploadImageType,
  id: string | number,
  progressCallback?: (progess: ManagedUpload.Progress) => void
): Promise<string> => {
  try {
    const params = {
      Bucket: AWS_S3_BUCKET,
      // Same collision fix as uploadFileWithPresignedUrl. This also starts honouring the
      // ORGANIZATION prefix, which this function previously fell through to `images/users/`.
      Key: buildObjectKey(type, id, image.name),
      Body: image,
      ACL: "public-read",
    };
    return await new Promise((resolve) => {
      const uploadRequest = s3.upload(
        params,
        function (err: unknown, data: unknown) {
          if (err) {
            throw err;
          }
          resolve(`${AWS_S3_BASE_URL}${params.Key}`);
        }
      );
      if (progressCallback)
        uploadRequest?.on("httpUploadProgress", progressCallback);
    });
  } catch (err) {
    console.error(err);
    throw err;
  }
};
/**
 * `uploadFile`, except that it rejects when the upload fails. `uploadFile` throws inside the SDK
 * callback, so its promise never settles on an error and a failed upload looks like one still in
 * progress. Used where a failed upload has to be shown and retried (the two-party wizard).
 */
export const uploadFileOrFail = (
  image: File,
  type: EUploadImageType,
  id: string | number,
  progressCallback?: (progess: ManagedUpload.Progress) => void
): Promise<string> => {
  const params = {
    Bucket: AWS_S3_BUCKET,
    Key: buildObjectKey(type, id, image.name),
    Body: image,
    ACL: "public-read",
  };
  return new Promise((resolve, reject) => {
    const uploadRequest = s3.upload(params, (err: unknown) => {
      if (err) reject(err);
      else resolve(`${AWS_S3_BASE_URL}${params.Key}`);
    });
    if (progressCallback)
      uploadRequest?.on("httpUploadProgress", progressCallback);
  });
};

export const getImageBufferByKey = async (key: string) => {
  const params = {
    Bucket: process.env.REACT_APP_AWS_S3_BUCKET || AWS_S3_BUCKET,
    Key: key,
  };
  return new Promise((resolve, reject) => {
    s3.getObject(params, (err, data) => {
      if (!err) {
        resolve(data.Body);
      } else {
        reject(err);
      }
    });
  });
};
